import { api } from "@/common/lib/api";
import type { CostCategory } from "@/features/cost-allocations/utils/categories";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

// ─── Types ────────────────────────────────────────────────────────────────
// Espejo del backend (src/features/received-invoices/{routes,service,schemas}.ts).
// Fechas: timestamptz → ISO string; numeric → string; bigint cents → number.

export type ReceivedInvoicePipelineStatus =
  | "received"
  | "ready"
  | "partially_assigned"
  | "assigned"
  | "quarantined"
  | "dismissed";

export type ReceivedInvoiceDocumentType = "FE" | "ND" | "NC" | "TE" | "FEC";

export type ReceivedInvoiceQuarantineReason =
  | "receptor_mismatch"
  | "xml_invalid"
  | "unsupported_type"
  | "no_xml_attachment";

export type ReceivedInvoiceTaxStatus =
  | "pending_confirmation"
  | "accepted"
  | "rejected"
  | "presumed_accepted";

export type ReceivedInvoiceRow = {
  id: string;
  accountId: string;
  clave: string | null;
  documentType: ReceivedInvoiceDocumentType | null;
  emisorTaxId: string | null;
  emisorName: string | null;
  emisorEmail: string | null;
  issuedAt: string | null;
  currency: string | null;
  exchangeRate: string | null;
  subtotalCents: number | null;
  taxCents: number | null;
  totalCents: number | null;
  condicionVenta: string | null;
  plazoCredito: string | null;
  medioPago: string | null;
  taxBreakdown: { code: string | null; rateCode: string | null; amountCents: number }[] | null;
  pipelineStatus: ReceivedInvoicePipelineStatus;
  quarantineReason: ReceivedInvoiceQuarantineReason | null;
  // Solo poblados en cuarentena no_xml_attachment (verificación de Gmail);
  // NULL para facturas y para filas anteriores a la parte 1.5.
  emailSubject: string | null;
  emailText: string | null;
  taxStatus: ReceivedInvoiceTaxStatus;
  haciendaMessageDetail: string | null;
  hasExoneration: boolean;
  r2XmlKey: string | null;
  r2HaciendaMsgKey: string | null;
  r2PdfKey: string | null;
  r2RawEmailKey: string | null;
  messageId: string | null;
  receivedAt: string;
  createdAt: string;
};

export type ReceivedInvoiceLineRow = {
  id: string;
  accountId: string;
  receivedInvoiceId: string;
  lineNumber: number;
  cabysCode: string | null;
  detail: string;
  quantity: string | null;
  lineTotalCents: number;
  taxCents: number;
  ivaRateCode: string | null;
  ivaRate: string | null;
  hasExoneration: boolean;
  suggestedCategory: CostCategory | null;
  createdAt: string;
};

export type ReceivedInvoiceAssignment = {
  id: string;
  costAllocationSourceEventId: string;
  amountCents: number;
  lotId: string | null;
  category: string | null;
  amountCrc: string | null;
  createdAt: string;
};

export type ReceivedInvoicesListResponse = {
  invoices: ReceivedInvoiceRow[];
};

export type ReceivedInvoiceDetailResponse = {
  invoice: ReceivedInvoiceRow;
  lines: ReceivedInvoiceLineRow[];
  assignments: ReceivedInvoiceAssignment[];
  unassignedCents: number | null;
};

export type ReceivedInvoicesListFilters = {
  status?: ReceivedInvoicePipelineStatus;
  from?: string; // YYYY-MM-DD
  to?: string;
};

// Split: montos en CENTAVOS de la MONEDA ORIGINAL de la factura (la regla
// Σ ≤ total se valida en esa moneda; la conversión a CRC la hace el backend
// con el exchange_rate del propio XML).
export type AssignmentSplitInput = {
  lotId: string;
  category: CostCategory;
  amountCents: number;
  periodStart?: string;
  periodEnd?: string;
};

export type CreateAssignmentsInput = {
  assignments: AssignmentSplitInput[];
  // Obligatorio SOLO si CondicionVenta del XML es ambigua (≠ 01/02).
  paymentMethod?: "cash" | "credit";
};

export type CreateAssignmentsResponse = {
  invoiceId: string;
  pipelineStatus: ReceivedInvoicePipelineStatus;
  assignedCents: number;
  unassignedCents: number;
  assignments: {
    id: string;
    costAllocationSourceEventId: string;
    amountCents: number;
    amountCrc: string;
    lotId: string;
    category: string;
  }[];
};

export type DeleteAssignmentResponse = {
  invoiceId: string;
  pipelineStatus: ReceivedInvoicePipelineStatus;
};

export type DismissReceivedInvoiceResponse = {
  id: string;
  pipelineStatus: ReceivedInvoicePipelineStatus;
};

export type ActivateMailboxResponse = {
  token: string;
  address: string;
  alreadyActive: boolean;
};

// ─── Query keys ───────────────────────────────────────────────────────────
// Toda la familia cuelga de ["received-invoices", accountId]: invalidar el
// prefijo refresca lista + badge + detalle de una vez.

export function receivedInvoicesQueryKey(accountId: string, filters?: ReceivedInvoicesListFilters) {
  return ["received-invoices", accountId, "list", filters ?? {}] as const;
}

export function receivedInvoiceDetailKey(accountId: string, invoiceId: string) {
  return ["received-invoices", accountId, "detail", invoiceId] as const;
}

export function readyInvoicesCountKey(accountId: string) {
  return ["received-invoices", accountId, "ready-count"] as const;
}

// ─── Queries ──────────────────────────────────────────────────────────────

function listPath(accountId: string, filters: ReceivedInvoicesListFilters): string {
  const params = new URLSearchParams();
  if (filters.status) params.set("status", filters.status);
  if (filters.from) params.set("from", filters.from);
  if (filters.to) params.set("to", filters.to);
  const qs = params.toString();
  return `/accounts/${accountId}/received-invoices${qs ? `?${qs}` : ""}`;
}

export function useReceivedInvoicesList(accountId: string, filters: ReceivedInvoicesListFilters) {
  return useQuery({
    queryKey: receivedInvoicesQueryKey(accountId, filters),
    queryFn: () => api.get<ReceivedInvoicesListResponse>(listPath(accountId, filters)),
    enabled: Boolean(accountId),
  });
}

// Badge del sidebar: conteo de facturas listas para asignar. No hay endpoint
// de conteo — se deriva de la lista filtrada por status=ready.
export function useReadyInvoicesCount(accountId: string) {
  return useQuery({
    queryKey: readyInvoicesCountKey(accountId),
    queryFn: () => api.get<ReceivedInvoicesListResponse>(listPath(accountId, { status: "ready" })),
    select: (data) => data.invoices.length,
    enabled: Boolean(accountId),
    staleTime: 30 * 1000,
  });
}

export function useReceivedInvoiceDetail(accountId: string, invoiceId: string | undefined) {
  return useQuery({
    queryKey: receivedInvoiceDetailKey(accountId, invoiceId ?? ""),
    queryFn: () =>
      api.get<ReceivedInvoiceDetailResponse>(
        `/accounts/${accountId}/received-invoices/${invoiceId}`,
      ),
    enabled: Boolean(accountId) && Boolean(invoiceId),
  });
}

// ─── Mutations ────────────────────────────────────────────────────────────

function invalidateAfterAssignmentChange(qc: ReturnType<typeof useQueryClient>, accountId: string) {
  // Lista + detalle + badge cuelgan del mismo prefijo.
  void qc.invalidateQueries({ queryKey: ["received-invoices", accountId] });
  // Cada split creó (o des-asignar borró) un costo prorrateado.
  void qc.invalidateQueries({ queryKey: ["costs", accountId] });
  // accumulated_cost de los animales del lote cambia tras el prorrateo.
  void qc.invalidateQueries({ queryKey: ["animals", accountId] });
}

export function useCreateAssignments(accountId: string, invoiceId: string) {
  const qc = useQueryClient();
  return useMutation({
    // `confirmUnusual` viaja como header X-Confirm-Unusual-Amount (mismo
    // contrato que useRegisterCost): el sanity-check de monto inusual del
    // motor de costos aplica a cada split.
    mutationFn: (input: CreateAssignmentsInput & { confirmUnusual?: boolean }) => {
      const { confirmUnusual, ...payload } = input;
      return api.post<CreateAssignmentsResponse>(
        `/accounts/${accountId}/received-invoices/${invoiceId}/assignments`,
        payload,
        confirmUnusual ? { headers: { "X-Confirm-Unusual-Amount": "true" } } : undefined,
      );
    },
    onSuccess: () => invalidateAfterAssignmentChange(qc, accountId),
  });
}

export function useDeleteAssignment(accountId: string, invoiceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (assignmentId: string) =>
      api.delete<DeleteAssignmentResponse>(
        `/accounts/${accountId}/received-invoices/${invoiceId}/assignments/${assignmentId}`,
      ),
    onSuccess: () => invalidateAfterAssignmentChange(qc, accountId),
  });
}

// Descarta un correo en cuarentena (idempotente en el backend: re-descartar
// un dismissed responde 200). Solo cuarentena — una factura real jamás se
// descarta; para eso existe des-asignar. Invalida el prefijo completo para
// refrescar lista + badge del sidebar de una vez.
export function useDismissReceivedInvoice(accountId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (invoiceId: string) =>
      api.post<DismissReceivedInvoiceResponse>(
        `/accounts/${accountId}/received-invoices/${invoiceId}/dismiss`,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["received-invoices", accountId] });
    },
  });
}

// Idempotente: si el buzón ya está activo devuelve la dirección existente
// (alreadyActive=true) sin rotar el token — sirve también como "ver dirección".
export function useActivateInboundMailbox(accountId: string) {
  return useMutation({
    mutationFn: () => api.post<ActivateMailboxResponse>(`/accounts/${accountId}/inbound-mailbox`),
  });
}

export function useSetTaxId(accountId: string) {
  return useMutation({
    mutationFn: (taxId: string) =>
      api.patch<{ taxId: string }>(`/accounts/${accountId}/tax-id`, { taxId }),
  });
}
